"use client";

import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  type ReactNode,
  type UIEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { cn } from "@/lib/utils";

const SCROLL_BOTTOM_THRESHOLD_PX = 24;

export const ActivityTerminal = memo(function ActivityTerminal({
  children,
  footer,
  className,
  bodyClassName,
  isWorking,
  scrollKey,
}: {
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  bodyClassName?: string;
  isWorking?: boolean;
  scrollKey?: string | number | null;
}) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const hasPositionedRef = useRef(false);
  const shouldReduceMotion = useReducedMotion();
  const resolvedScrollKey = useMemo(() => scrollKey ?? null, [scrollKey]);

  const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const node = event.currentTarget;
    const distanceFromBottom =
      node.scrollHeight - node.scrollTop - node.clientHeight;
    stickToBottomRef.current = distanceFromBottom <= SCROLL_BOTTOM_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    const node = contentRef.current;
    if (!node || !stickToBottomRef.current) return;
    node.scrollTo({
      top: node.scrollHeight,
      behavior:
        shouldReduceMotion || !hasPositionedRef.current ? "auto" : "smooth",
    });
    hasPositionedRef.current = true;
  }, [resolvedScrollKey, shouldReduceMotion]);

  if (!children && !footer) return null;

  return (
    <div className={cn("relative", className)}>
      <AnimatePresence>
        {isWorking ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="pointer-events-none absolute -inset-1.5 z-0"
          >
            <motion.div
              animate={{ opacity: [0.55, 0.95, 0.55] }}
              transition={{
                duration: 3,
                ease: "easeInOut",
                repeat: Number.POSITIVE_INFINITY,
              }}
              className="h-full w-full rounded-2xl bg-primary/20 blur-xl"
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="relative z-10 h-full rounded-xl p-[2px]">
        <div className="absolute inset-0 overflow-hidden rounded-xl">
          <div
            className={cn(
              "absolute inset-0 rounded-xl bg-border/70 transition-opacity duration-300",
              isWorking ? "opacity-0" : "opacity-100",
            )}
          />
          <AnimatePresence>
            {isWorking ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, rotate: 360 }}
                exit={{ opacity: 0 }}
                transition={{
                  opacity: { duration: 0.25 },
                  rotate: {
                    duration: 4,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "linear",
                  },
                }}
                style={{
                  background:
                    "conic-gradient(from 0deg, transparent 60%, hsl(var(--primary) / 0.5) 80%, hsl(var(--primary) / 1) 90%, hsl(var(--primary) / 0.5) 96%, transparent 100%)",
                }}
                className="absolute inset-[-100%]"
              />
            ) : null}
          </AnimatePresence>
        </div>

        <div className="relative z-10 h-full overflow-hidden rounded-[10px] bg-surface text-foreground shadow-sm backdrop-blur-xl">
          <div
            ref={contentRef}
            onScroll={handleScroll}
            className={cn(
              "min-h-[56px] max-h-[84px] overflow-y-auto scrollbar-thin px-2 py-2 font-mono text-xs leading-5",
              bodyClassName,
            )}
          >
            {children}
          </div>
          {footer ? <div className="px-3 py-1">{footer}</div> : null}
        </div>
      </div>
    </div>
  );
});

export function FloatingActivityTerminal({
  children,
  className,
  panelClassName,
  showOnMobile = false,
}: {
  children: ReactNode;
  className?: string;
  panelClassName?: string;
  showOnMobile?: boolean;
}) {
  return (
    <div
      className={cn(
        // `right` is owned by globals.css (var(--mlx-artifact-panel-width)) so
        // the overlay hugs the chat column when a side panel is open — a
        // right-0 utility here would out-cascade that rule.
        "mlx-floating-activity-terminal pointer-events-none absolute bottom-full left-0 z-20 mb-3 min-w-0 justify-center",
        showOnMobile ? "flex" : "hidden lg:flex",
        className,
      )}
    >
      <div
        className={cn(
          "pointer-events-auto min-w-0 w-full max-w-[min(800px,100%)]",
          panelClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
